import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IMusicProductDigitalAssetId,
  IMusicProductDigitalAssetIdsAutoAssignResponse,
} from './types/query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import {
  IAutoAssignMusicProductDigitalAssetIds,
  ICreateMusicProductDigitalAssetId,
} from './types/mutation';

export const useGetMusicProductDigitalAssetId = (
  DigitalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMusicProductDigitalAssetId[], DefaultError>({
    queryKey: ['mpd-assets-id', DigitalMusicProductId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/AssetIds`,
        params: { DigitalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalAssetId`,
      });
    },
  });
};

export const usePostMusicProductDigitalAssetId = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigitalAssetId'
  );
  return useMutation<
    unknown,
    DefaultError,
    ICreateMusicProductDigitalAssetId
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('music-product-digital.asset-id-what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductDigital/Deliveries/Track/AssetId',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalAssetId',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what:  i18next.t('music-product-digital.asset-id-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostMusicProductDigitalAssetIdsAutoAssign = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<
    IMusicProductDigitalAssetIdsAutoAssignResponse,
    DefaultError,
    IAutoAssignMusicProductDigitalAssetIds
  >({
    mutationFn: async body => {
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductDigital/AssetIds/AutoAssign',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalAssetIdsAutoAssign',
      });
    },
  });
};
